// Contracts — the client agreements that anchor billing. A filterable list
// (by client org, status and "expiring before") on the left of a create/edit
// modal flow. Each contract carries a number, term window, total value and a
// billing cycle that drives recurring invoicing.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import type {
  BillingCycle,
  Contract,
  ContractStatus,
  Organization,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import { useOrganizations } from '../api/queries/clients';
import {
  useContracts,
  useCreateContract,
  useUpdateContract,
  useDeleteContract,
  type ContractFilters,
} from '../api/queries/billing';

// ---------- Static option lists ----------

const STATUSES: { value: ContractStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
];

const CYCLES: { value: BillingCycle; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'per_event', label: 'Per event' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null): string {
  return epochMs === null ? '—' : new Date(epochMs).toLocaleDateString();
}

function fmtMoney(value: string | null): string {
  if (value === null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

// Contract status → shared Badge tone (colors match the old badge-* set).
function statusTone(status: ContractStatus): BadgeTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warn';
    case 'expired':
    case 'terminated':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function statusLabel(status: ContractStatus): string {
  return STATUSES.find((s) => s.value === status)?.label ?? status;
}

function cycleLabel(cycle: BillingCycle): string {
  return CYCLES.find((c) => c.value === cycle)?.label ?? cycle;
}

// =============================================================================
// Page
// =============================================================================

export function ContractsPage(): JSX.Element {
  const canWrite = can('contract.write');

  const [filters, setFilters] = useState<ContractFilters>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [deleting, setDeleting] = useState<Contract | null>(null);

  const contractsQuery = useContracts(filters);
  const orgsQuery = useOrganizations();
  const deleteContract = useDeleteContract();

  const contracts = contractsQuery.data?.contracts ?? [];
  const orgs = orgsQuery.data?.organizations ?? [];

  const orgName = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of orgs) map.set(o.id, o.name);
    return (id: number): string => map.get(id) ?? `Org #${id}`;
  }, [orgs]);

  // Standalone filter selects use the literal 'all' sentinel (Radix Select
  // forbids an empty value); the filter logic treats 'all' as "no filter".
  const clientFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All clients' },
    ...orgs.map((o) => ({ value: String(o.id), label: o.name })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteContract.mutateAsync(deleting.id);
      toast.success(`Deleted contract ${deleting.contract_no}.`);
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete contract.'));
    }
  };

  return (
    <div className="contracts-page">
      <header className="page-header">
        <div>
          <h2>Contracts</h2>
          <p className="page-sub">Client agreements and their billing terms.</p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New contract
          </Button>
        )}
      </header>

      <div className="page-toolbar">
        <label className="form-field inline">
          <span>Client</span>
          <Select
            value={filters.organization_id ? String(filters.organization_id) : 'all'}
            onValueChange={(v) =>
              setFilters((f) => ({
                ...f,
                organization_id: v === 'all' ? undefined : Number(v),
              }))
            }
            options={clientFilterOptions}
            aria-label="Filter by client"
          />
        </label>

        <label className="form-field inline">
          <span>Status</span>
          <Select
            value={filters.status ?? 'all'}
            onValueChange={(v) =>
              setFilters((f) => ({
                ...f,
                status: v === 'all' ? undefined : (v as ContractStatus),
              }))
            }
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </label>

        <label className="form-field inline">
          <span>Expiring before</span>
          <DateInput
            value={filters.expiring_before ?? null}
            onChange={(epoch) =>
              setFilters((f) => ({ ...f, expiring_before: epoch ?? undefined }))
            }
          />
        </label>
      </div>

      {contractsQuery.isLoading && <p className="muted">Loading contracts…</p>}
      {contractsQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(contractsQuery.error, 'Failed to load contracts.')}
        </p>
      )}
      {!contractsQuery.isLoading && contracts.length === 0 && (
        <p className="muted empty-state">No contracts match these filters.</p>
      )}

      {contracts.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Contract #</Table.HeaderCell>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Cycle</Table.HeaderCell>
              <Table.HeaderCell>Term</Table.HeaderCell>
              <Table.HeaderCell>Value</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              {canWrite && (
                <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {contracts.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell tone="mono">{c.contract_no}</Table.Cell>
                <Table.Cell>{orgName(c.organization_id)}</Table.Cell>
                <Table.Cell tone="strong" className="cell-truncate">
                  {c.title ?? '—'}
                </Table.Cell>
                <Table.Cell>
                  <Badge tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                </Table.Cell>
                <Table.Cell>{cycleLabel(c.billing_cycle)}</Table.Cell>
                <Table.Cell>
                  {fmtDate(c.start_at)} – {fmtDate(c.end_at)}
                </Table.Cell>
                <Table.Cell>{fmtMoney(c.value)}</Table.Cell>
                <Table.Cell>{fmtDate(c.expires_at)}</Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button variant="ghost" onClick={() => setEditing(c)}>
                      Edit
                    </Button>
                    <Button variant="danger" onClick={() => setDeleting(c)}>
                      Delete
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Create */}
      {createOpen && (
        <ContractFormModal
          mode="create"
          orgs={orgs}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {/* Edit */}
      {editing && (
        <ContractFormModal
          mode="edit"
          contract={editing}
          orgs={orgs}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Delete */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete contract"
        message={
          deleting
            ? `Delete contract ${deleting.contract_no}? This can be restored only by an administrator.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteContract.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// =============================================================================
// Create / edit modal
// =============================================================================

interface ContractFormModalProps {
  mode: 'create' | 'edit';
  contract?: Contract;
  orgs: Organization[];
  onClose: () => void;
}

function ContractFormModal({
  mode,
  contract,
  orgs,
  onClose,
}: ContractFormModalProps): JSX.Element {
  const create = useCreateContract();
  const update = useUpdateContract();
  const busy = create.isPending || update.isPending;

  const [organizationId, setOrganizationId] = useState<number | ''>(
    contract?.organization_id ?? '',
  );
  const [contractNo, setContractNo] = useState(contract?.contract_no ?? '');
  const [title, setTitle] = useState(contract?.title ?? '');
  const [status, setStatus] = useState<ContractStatus>(contract?.status ?? 'draft');
  const [cycle, setCycle] = useState<BillingCycle>(
    contract?.billing_cycle ?? 'monthly',
  );
  const [startAt, setStartAt] = useState<number | null>(contract?.start_at ?? null);
  const [endAt, setEndAt] = useState<number | null>(contract?.end_at ?? null);
  const [expiresAt, setExpiresAt] = useState<number | null>(
    contract?.expires_at ?? null,
  );
  const [value, setValue] = useState(contract?.value ?? '');
  const [notes, setNotes] = useState(contract?.notes ?? '');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedNo = contractNo.trim();
    if (!trimmedNo) {
      toast.warn('Contract number is required.');
      return;
    }
    const trimmedValue = value.trim();
    if (trimmedValue && !/^\d+(\.\d{1,2})?$/.test(trimmedValue)) {
      toast.warn('Value must be a non-negative amount (e.g. 12000.00).');
      return;
    }
    const trimmedTitle = title.trim();
    const trimmedNotes = notes.trim();

    try {
      if (mode === 'create') {
        if (organizationId === '') {
          toast.warn('Select a client.');
          return;
        }
        await create.mutateAsync({
          organization_id: organizationId,
          contract_no: trimmedNo,
          status,
          billing_cycle: cycle,
          ...(trimmedTitle ? { title: trimmedTitle } : {}),
          ...(startAt !== null ? { start_at: startAt } : {}),
          ...(endAt !== null ? { end_at: endAt } : {}),
          ...(expiresAt !== null ? { expires_at: expiresAt } : {}),
          ...(trimmedValue ? { value: trimmedValue } : {}),
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        });
        toast.success(`Created contract ${trimmedNo}.`);
      } else if (contract) {
        await update.mutateAsync({
          id: contract.id,
          contract_no: trimmedNo,
          status,
          billing_cycle: cycle,
          title: trimmedTitle ? trimmedTitle : null,
          start_at: startAt,
          end_at: endAt,
          expires_at: expiresAt,
          value: trimmedValue ? trimmedValue : null,
          notes: trimmedNotes ? trimmedNotes : null,
        });
        toast.success('Contract updated.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save contract.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New contract' : `Edit ${contract?.contract_no}`}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="contract-form"
            variant="primary"
            disabled={busy}
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="contract-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Client">
          <select
            className="form-input"
            value={organizationId}
            onChange={(e) =>
              setOrganizationId(e.target.value ? Number(e.target.value) : '')
            }
            disabled={mode === 'edit'}
            required={mode === 'create'}
          >
            <option value="">Select a client…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Contract number">
          <Input
            type="text"
            value={contractNo}
            onChange={(e) => setContractNo(e.target.value)}
            maxLength={120}
            required
            autoFocus
          />
        </FormField>

        <FormField label="Title">
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={300}
            placeholder="Optional"
          />
        </FormField>

        <FormRow>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as ContractStatus)}
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Billing cycle">
            <select
              className="form-input"
              value={cycle}
              onChange={(e) => setCycle(e.target.value as BillingCycle)}
            >
              {CYCLES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Start date">
            <DateInput value={startAt} onChange={setStartAt} />
          </FormField>
          <FormField label="End date">
            <DateInput value={endAt} onChange={setEndAt} />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Expires">
            <DateInput value={expiresAt} onChange={setExpiresAt} />
          </FormField>
          <FormField label="Value (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0.00"
            />
          </FormField>
        </FormRow>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={20_000}
            placeholder="Optional"
          />
        </FormField>
      </form>
    </Dialog>
  );
}
